import api from '@/lib/api'
import type { ApiResponse } from '@/types'
import type { Task, TaskUserRef } from '@/lib/tasks-api'

export type ContactOutcome =
  | 'no_answer' | 'busy' | 'unavailable' | 'voicemail' | 'third_party' | 'technical_failure'
  | 'callback_requested' | 'interested' | 'needs_information' | 'awaiting_documents'
  | 'needs_clinical_review' | 'wants_quote' | 'wants_booking' | 'interested_later' | 'concern'
  | 'contact_correction' | 'language_mismatch' | 'specialist_review' | 'channel_preference'
  | 'enquiry_unrecognized' | 'existing_patient' | 'complaint' | 'clinical_concern'
  | 'duplicate_or_invalid_enquiry'
  | 'enquiry_closed' | 'do_not_call'
export type ContactResultGroup =
  | 'not_reached' | 'callback' | 'conversation' | 'correction' | 'routing' | 'closure' | 'restriction'
export type ContactInteraction = 'attempted' | 'conversation' | 'none'
export interface ContactOutcomeChoice {
  key: string
  outcome: ContactOutcome
  label: string
  group?: ContactResultGroup
  requires_internal?: boolean
  requires_clinician?: boolean
  requires_note?: boolean
  requires_interaction?: boolean
  allowed_interactions?: ContactInteraction[]
  next_step_kind?: 'contact' | 'review' | 'retained' | 'none'
}
export interface LeadTaskDependency {
  restricted: boolean
  task: Task | null
}
export interface LeadContactOutcome {
  system_key?: string
  id: string
  outcome: ContactOutcome
  note: string | null
  occurred_at: string
  actor: TaskUserRef | null
  completed_task_id: string | null
  next_task_id: string | null
  interaction?: ContactInteraction
  details?: Record<string, unknown>
}
export interface LeadProgressEvent {
  id: string
  previous_status_id: string | null
  status_id: string
  reason: string
  occurred_at: string
  actor: TaskUserRef | null
}
export interface RecordLeadProgress {
  request_id: string
  lead_version: number
  context_token: string
  status_id: string
  reason: string
}
export async function recordLeadProgress(leadId: string, data: RecordLeadProgress) {
  const response = await api.post<
    ApiResponse<{ progress: LeadProgressEvent; lead_version: number }>
  >(`/leads/${leadId}/progress`, data)
  return response.data.data
}
export interface LeadNextStep {
  can_schedule?: boolean
  can_reschedule?: boolean
  can_view_history?: boolean
  next_action_assignees?: TaskUserRef[]
  can_update_stage?: boolean
  stage_choices?: {
    id: string
    label: string
    system_key: string | null
    kind: 'reopen' | 'close'
  }[]
  last_progress?: LeadProgressEvent | null

  stage?: {
    id: string
    label: string
    system_key: string | null
    mapping_state: 'mapped' | 'legacy_unmapped'
  } | null
  contact_choices?: ContactOutcomeChoice[]
  outcome_assignees?: TaskUserRef[]
  internal_assignees?: TaskUserRef[]
  clinical_assignees?: TaskUserRef[]
  dependencies?: LeadTaskDependency[]
  phone_correction_required?: boolean

  lead_id: string
  version: number
  context_token: string
  eligibility: { can_record: boolean; reason: 'do_not_call' | 'phone_correction_required' | 'closed' | 'status_unknown' | null }
  next_step: Task | null
  restricted_next_step: boolean
  last_outcome: LeadContactOutcome | null
  can_record: boolean
}

export interface NextActionContext {
  request_id: string
  lead_version: number
  context_token: string
  current_task_id: string | null
  current_task_version: number | null
}
export interface ScheduleNextAction extends NextActionContext {
  next_step: { title: string; assigned_to: number; due_at: string | null }
}
export interface RescheduleNextAction extends NextActionContext {
  due_at: string
  reason: string
}
interface NextActionResponse {
  event: {
    id: string
    operation: 'schedule' | 'reschedule'
    task_id: string
    reason: string | null
    occurred_at: string
  }
  next_step: Task | null
  restricted_next_step: boolean
  lead_version: number
}
export async function scheduleNextAction(leadId: string, data: ScheduleNextAction) {
  const response = await api.post<ApiResponse<NextActionResponse>>(
    `/leads/${leadId}/next-step`,
    data
  )
  return response.data.data
}
export async function rescheduleNextAction(leadId: string, data: RescheduleNextAction) {
  const response = await api.post<ApiResponse<NextActionResponse>>(
    `/leads/${leadId}/next-step/reschedule`,
    data
  )
  return response.data.data
}
export interface RecordContactOutcome {
  request_id: string
  lead_version: number
  context_token: string
  current_task_id: string | null
  current_task_version: number | null
  outcome: ContactOutcome
  note: string | null
  next_step?: { title: string; due_at: string; assigned_to: number }
  internal_task?: { title: string; due_at: string; assigned_to: number }
  interaction?: ContactInteraction
  status_id?: string
  voicemail_left?: boolean
  contact_channel?: 'phone' | 'email' | 'whatsapp'
  correction_target?: 'primary_phone' | 'other_details'
}
export async function getLeadNextStep(leadId: string, signal?: AbortSignal) {
  const response = await api.get<ApiResponse<LeadNextStep>>(`/leads/${leadId}/next-step`, {
    signal
  })
  return response.data.data
}
export async function recordContactOutcome(leadId: string, data: RecordContactOutcome) {
  const response = await api.post<
    ApiResponse<{
      outcome: LeadContactOutcome
      completed_task: Task | null
      next_step: Task | null
      internal_task?: Task | null
      lead_version: number
    }>
  >(`/leads/${leadId}/contact-outcomes`, data)
  return response.data.data
}

// Moved to a dependency-free module so it is unit-testable; re-exported for
// the existing callers.
export { localCommitmentToIso } from '@/lib/local-datetime'
